import { Suspense, lazy, useEffect } from "react";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { Toaster } from "@/components/ui/sonner";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, Redirect } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UploadStatusBar } from "./components/UploadStatusBar";
import { ReportIssueButton } from "./components/ReportIssueButton";
import DashboardLayout from "./components/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { RoleGuard, FINANCIAL_ROLES } from "./components/RoleGuard";
// OWNER_GROWTH_EXPORT_BEGIN
const GrowthWorkspace = lazy(() => import("./ownerGrowth/GrowthWorkspace"));
// OWNER_GROWTH_EXPORT_END

// Lazy load all page components for better initial load performance
const Home = lazy(() => import("./pages/Home"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Estimator = lazy(() => import("./pages/Estimator"));
const Materials = lazy(() => import("./pages/Materials"));
const LaborRates = lazy(() => import("./pages/LaborRates"));
const Vendors = lazy(() => import("./pages/Vendors"));
const Brands = lazy(() => import("./pages/Brands"));
const ScopeOfWork = lazy(() => import("./pages/ScopeOfWork"));
const Takeoffs = lazy(() => import("./pages/Takeoffs"));
const Templates = lazy(() => import("./pages/Templates"));
const Leads = lazy(() => import("./pages/Leads"));
const Receipts = lazy(() => import("./pages/Receipts"));
const AllReceipts = lazy(() => import("./pages/AllReceipts"));
const Bills = lazy(() => import("./pages/Bills"));
const EstimateView = lazy(() => import("./pages/EstimateView"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const Subcontractors = lazy(() => import("./pages/Subcontractors"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const MediaLibrary = lazy(() => import("./pages/MediaLibrary"));
const VoiceRecordings = lazy(() => import("./pages/VoiceRecordings"));
const ProjectDocuments = lazy(() => import("./pages/ProjectDocuments"));
const FormTemplates = lazy(() => import("./pages/FormTemplates"));
const MasterLists = lazy(() => import("./pages/MasterLists"));
const UnassignedPhotos = lazy(() => import("./pages/UnassignedPhotos"));
const WCReport = lazy(() => import("./pages/WCReport"));
const Proposal = lazy(() => import("./pages/Proposal"));
const DailyReports = lazy(() => import("./pages/DailyReports"));
const ProjectTimeline = lazy(() => import("./pages/ProjectTimeline"));
const Invoices = lazy(() => import("./pages/Invoices"));
const ChangeOrders = lazy(() => import("./pages/ChangeOrders"));
const PunchList = lazy(() => import("./pages/PunchList"));
const ProgressBilling = lazy(() => import("./pages/ProgressBilling"));
const SubBills = lazy(() => import("./pages/SubBills"));
const ChartOfAccounts = lazy(() => import("./pages/ChartOfAccounts"));
const JobCosting = lazy(() => import("./pages/JobCosting"));
const ProfitLoss = lazy(() => import("./pages/ProfitLoss"));
const FinancialSummary = lazy(() => import("./pages/FinancialSummary"));
const Report1099 = lazy(() => import("./pages/Report1099"));
const CrewReports = lazy(() => import("./pages/CrewReports"));
const BankAccounts = lazy(() => import("./pages/BankAccounts"));
const Transactions = lazy(() => import("./pages/Transactions"));
const TransactionReview = lazy(() => import("./pages/TransactionReview"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const VendorBills = lazy(() => import("./pages/VendorBills"));
const BillProcessingQueue = lazy(() => import("./pages/BillProcessingQueue"));
const SubPortalBills = lazy(() => import("./pages/SubPortalBills"));
const SubPortalPayments = lazy(() => import("./pages/SubPortalPayments"));
const SubPortalProjects = lazy(() => import("./pages/SubPortalProjects"));
const CompanySettings = lazy(() => import("./pages/CompanySettings"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
const SocialMedia = lazy(() => import("./pages/SocialMedia"));
const SocialAccounts = lazy(() => import("./pages/SocialAccounts"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const SharedScope = lazy(() => import("./pages/SharedScope"));
const SharedPurchaseOrder = lazy(() => import("./pages/SharedPurchaseOrder"));
const SharedInvoice = lazy(() => import("./pages/SharedInvoice"));
const InvoicePaid = lazy(() => import("./pages/InvoicePaid"));
const Trash = lazy(() => import("./pages/Trash"));
const DeletionRequests = lazy(() => import("./pages/DeletionRequests"));
const EmailReceipts = lazy(() => import("./pages/EmailReceipts"));
const ActivityLog = lazy(() => import("./pages/ActivityLog"));
const Handbook = lazy(() => import("./pages/Handbook"));
const ProjectTemplates = lazy(() => import("./pages/ProjectTemplates"));
const RecurringExpenses = lazy(() => import("./pages/RecurringExpenses"));
const BudgetAlerts = lazy(() => import("./pages/BudgetAlerts"));
const AILearning = lazy(() => import("./pages/AILearning"));
const SubBillingPortal = lazy(() => import("./pages/SubBillingPortal"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const SmsInquiriesPage = lazy(() => import("./pages/SmsInquiriesPage"));
const TransactionMatchingPage = lazy(() => import("./pages/TransactionMatchingPage"));
const AuditHistory = lazy(() => import("./pages/AuditHistory"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Backup = lazy(() => import("./pages/Backup"));
const BidManagement = lazy(() => import("./pages/BidManagement"));
const Banking = lazy(() => import("./pages/Banking"));
const EmailLog = lazy(() => import("./pages/EmailLog"));
const ClientInbox = lazy(() => import("./pages/ClientInbox"));
const InboundEmailLog = lazy(() => import("./pages/InboundEmailLog"));
const Communications = lazy(() => import("./pages/Communications"));
const FixedAssets = lazy(() => import("./pages/FixedAssets"));
const BalanceSheet = lazy(() => import("./pages/BalanceSheet"));
const SubBidSubmit = lazy(() => import("./pages/SubBidSubmit"));
const PunchListPublic = lazy(() => import("./pages/PunchListPublic"));
const OpeningBalances = lazy(() => import("./pages/OpeningBalances"));
const BankReconciliation = lazy(() => import("./pages/BankReconciliation"));
const Compliance = lazy(() => import("./pages/Compliance"));
const SubPortalCompliance = lazy(() => import("./pages/SubPortalCompliance"));
const SubOnboarding = lazy(() => import("./pages/SubOnboarding"));
const VendorOnboardingPortal = lazy(() => import("./pages/VendorOnboardingPortal"));
const PaymentQueue = lazy(() => import("./pages/PaymentQueue"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const Tax1099 = lazy(() => import("./pages/Tax1099"));
const SharedEstimate = lazy(() => import("./pages/SharedEstimate"));
const SharedChangeOrder = lazy(() => import("./pages/SharedChangeOrder"));
const InvoiceEditor = lazy(() => import("./pages/InvoiceEditor"));
const ActionQueue = lazy(() => import("./pages/ActionQueue"));
const AiChat = lazy(() => import("./pages/AiChat"));
const AiDailyReports = lazy(() => import("./pages/AiDailyReports"));
const AiInsights = lazy(() => import("./pages/AiInsights"));
const IntelligenceDashboard = lazy(() => import("./pages/IntelligenceDashboard"));
const AiMemory = lazy(() => import("./pages/AiMemory"));
const AiInvoices = lazy(() => import("./pages/AiInvoices"));
const AiChangeOrders = lazy(() => import("./pages/AiChangeOrders"));
const SubBidCompare = lazy(() => import("./pages/SubBidCompare"));
const SummaryReports = lazy(() => import("./pages/SummaryReports"));
const QuickActions = lazy(() => import("./pages/QuickActions"));
const ClientApprovals = lazy(() => import("./pages/ClientApprovals"));
const SecurityCenter = lazy(() => import("./pages/SecurityCenter"));
const DataIntegrity = lazy(() => import("./pages/DataIntegrity"));
const FieldPermissions = lazy(() => import("./pages/FieldPermissions"));
const ClientApprovalPage = lazy(() => import("./pages/ClientApprovalPage"));
const CompanyDashboard = lazy(() => import("./pages/CompanyDashboard"));
const JournalEntries = lazy(() => import("./pages/JournalEntries"));
const AccountRegister = lazy(() => import("./pages/AccountRegister"));
const AgingReport = lazy(() => import("./pages/AgingReport"));
const OrderLists = lazy(() => import("./pages/OrderLists"));
const VendorCredits = lazy(() => import("./pages/VendorCredits"));
const MaterialTracking = lazy(() => import("./pages/MaterialTracking"));
const ProjectChat = lazy(() => import("./pages/ProjectChat"));
const LoanManager = lazy(() => import("./pages/LoanManager"));
const SalesTax = lazy(() => import("./pages/SalesTax"));
const MileageTracker = lazy(() => import("./pages/MileageTracker"));
const CostTrends = lazy(() => import("./pages/CostTrends"));
const AccountantAccess = lazy(() => import("./pages/AccountantAccess"));
const IssuesDashboard = lazy(() => import("./pages/IssuesDashboard"));
const BidTracker = lazy(() => import("./pages/BidTracker"));
const RealtorOutreach = lazy(() => import("./pages/RealtorOutreach"));
const Submittals = lazy(() => import("./pages/Submittals"));
const LeadSources = lazy(() => import("./pages/LeadSources"));
const DrawingTool = lazy(() => import("./pages/DrawingTool"));
const Notifications = lazy(() => import("./pages/Notifications"));
const StripeDashboard = lazy(() => import("./pages/StripeDashboard"));
const CommandCenter = lazy(() => import("./pages/CommandCenter"));
const Commissions = lazy(() => import("./pages/Commissions"));
const SmsSettings = lazy(() => import("./pages/SmsSettings"));
const SmsPermissions = lazy(() => import("./pages/SmsPermissions"));
const Clients = lazy(() => import("./pages/Clients"));
const SmsHistory = lazy(() => import("./pages/SmsHistory"));
const SmsPhotoLog = lazy(() => import("./pages/SmsPhotoLog"));
const SmsTerms = lazy(() => import("./pages/SmsTerms"));
const SmsPrivacy = lazy(() => import("./pages/SmsPrivacy"));
const SmsOptInPreview = lazy(() => import("./pages/SmsOptInPreview"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Schedule = lazy(() => import("./pages/Schedule"));
const TimeTracking = lazy(() => import("./pages/TimeTracking"));
const DailyCostLog = lazy(() => import("./pages/DailyCostLog"));
const CostPlusInvoice = lazy(() => import("./pages/CostPlusInvoice"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LienWaivers = lazy(() => import("./pages/LienWaivers"));
const Warranties = lazy(() => import("./pages/Warranties"));
const NotificationPreferences = lazy(() => import("./pages/NotificationPreferences"));
const EmailTemplates = lazy(() => import("./pages/EmailTemplates"));
const AdvancedSearch = lazy(() => import("./pages/AdvancedSearch"));
const QuickBooksMigration = lazy(() => import("./pages/QuickBooksMigration"));
const HistoricalPL = lazy(() => import("./pages/HistoricalPL"));

function PageLoader() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-96" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
      </div>
      <Skeleton className="h-64 rounded-lg mt-4" />
    </div>
  );
}

function Router() {
  return (
    <Switch>
      {/* Narrow, expiring expense review capability; no general banking access. */}
      <Route path="/review-transaction">
        <Suspense fallback={<PageLoader />}><TransactionReview /></Suspense>
      </Route>
      {/* Client-facing estimate view - no dashboard layout */}
      <Route path="/estimate/:id/view">
        <Suspense fallback={<PageLoader />}>
          <EstimateView />
        </Suspense>
      </Route>
      <Route path="/estimate/:id/proposal">
        <Suspense fallback={<PageLoader />}>
          <Proposal />
        </Suspense>
      </Route>
      {/* Client Portal - public, no dashboard layout */}
      <Route path="/portal/:token">
        <Suspense fallback={<PageLoader />}>
          <ClientPortal />
        </Suspense>
      </Route>
      {/* Shared Scope - public, no dashboard layout (for subs, no pricing) */}
      <Route path="/shared-scope/:token">
        <Suspense fallback={<PageLoader />}>
          <SharedScope />
        </Suspense>
      </Route>
      {/* Invoice payment success page */}
      <Route path="/invoice-paid">
        <Suspense fallback={<PageLoader />}>
          <InvoicePaid />
        </Suspense>
      </Route>
      {/* Shared Invoice - public, with Pay Now button */}
      <Route path="/shared-invoice/:token">
        <Suspense fallback={<PageLoader />}>
          <SharedInvoice />
        </Suspense>
      </Route>
      {/* Shared Estimate - public live estimate view */}
      <Route path="/shared-estimate/:token">
        <Suspense fallback={<PageLoader />}>
          <SharedEstimate />
        </Suspense>
      </Route>
      {/* Shared Change Order - public live change order view */}
      <Route path="/shared-change-order/:token">
        <Suspense fallback={<PageLoader />}>
          <SharedChangeOrder />
        </Suspense>
      </Route>
      {/* Shared Purchase Order - public, no dashboard layout (for subs) */}
      <Route path="/shared-po/:token">
        <Suspense fallback={<PageLoader />}>
          <SharedPurchaseOrder />
        </Suspense>
      </Route>
      {/* Sub Billing Portal - public, no dashboard layout */}
      <Route path="/sub-portal/:token">
        <Suspense fallback={<PageLoader />}>
          <SubBillingPortal />
        </Suspense>
      </Route>
      {/* Sub Compliance Portal - public, no dashboard layout */}
      <Route path="/sub-portal/compliance/:token">
        <Suspense fallback={<PageLoader />}>
          <SubPortalCompliance />
        </Suspense>
      </Route>
      {/* Sub Onboarding Portal - public, no dashboard layout */}
      <Route path="/sub-onboarding/:token">
        <Suspense fallback={<PageLoader />}>
          <SubOnboarding />
        </Suspense>
      </Route>
      {/* Vendor Onboarding Portal - public, no dashboard layout */}
      <Route path="/vendor-onboarding/:token">
        <Suspense fallback={<PageLoader />}>
          <VendorOnboardingPortal />
        </Suspense>
      </Route>
      {/* Change password - no dashboard layout */}
      <Route path="/change-password">
        <Suspense fallback={<PageLoader />}>
          <ChangePassword />
        </Suspense>
      </Route>
      {/* Reset password via token link - no dashboard layout */}
      <Route path="/reset-password">
        <Suspense fallback={<PageLoader />}>
          <ResetPassword />
        </Suspense>
      </Route>
      {/* Client approval - public, no auth required */}
      <Route path="/approve/:token">
        <Suspense fallback={<PageLoader />}>
          <ClientApprovalPage />
        </Suspense>
      </Route>
      {/* Sub bid submission - public, no auth required */}
      <Route path="/sub-bid/:token">
        <Suspense fallback={<PageLoader />}>
          <SubBidSubmit />
        </Suspense>
      </Route>
      {/* SMS Terms - public consent page for SMS compliance */}
      <Route path="/sms-terms">
        <Suspense fallback={<PageLoader />}>
          <SmsTerms />
        </Suspense>
      </Route>
      {/* Messaging policy and proposed enrollment proof - no login, no real sign-up. */}
      <Route path="/sms-privacy">
        <Suspense fallback={<PageLoader />}><SmsPrivacy /></Suspense>
      </Route>
      <Route path="/sms-opt-in-preview">
        <Suspense fallback={<PageLoader />}><SmsOptInPreview /></Suspense>
      </Route>
      {/* Privacy Policy - public, no auth required */}
      <Route path="/privacy">
        <Suspense fallback={<PageLoader />}>
          <Privacy />
        </Suspense>
      </Route>
      {/* Terms and Conditions - public, no auth required */}
      <Route path="/terms">
        <Suspense fallback={<PageLoader />}>
          <Terms />
        </Suspense>
      </Route>
      {/* Public punch list for subs - no auth required */}
      <Route path="/punch-list-public/:token">
        <Suspense fallback={<PageLoader />}>
          <PunchListPublic />
        </Suspense>
      </Route>
      {/* All other routes wrapped in dashboard */}
      <Route>
        <DashboardLayout>
          <Suspense fallback={<PageLoader />}>
            <Switch>
              <Route path="/" component={Home} />
              <Route path="/dashboard">{() => <Redirect to="/" />}</Route>
              <Route path="/command-center" component={CommandCenter} />
              <Route path="/company-dashboard" component={CompanyDashboard} />
              <Route path="/projects" component={Projects} />
              <Route path="/projects/:id" component={ProjectDetail} />
              <Route path="/estimator" component={Estimator} />
              <Route path="/estimator/:projectId" component={Estimator} />
              <Route path="/materials" component={Materials} />
              <Route path="/labor-rates" component={LaborRates} />
              <Route path="/vendors" component={Vendors} />
              <Route path="/brands" component={Brands} />
              <Route path="/scope-of-work" component={ScopeOfWork} />
              <Route path="/takeoffs" component={Takeoffs} />
              <Route path="/templates" component={Templates} />
              <Route path="/leads" component={Leads} />
              {/* OWNER_GROWTH_EXPORT_BEGIN */}
              <Route path="/leads-outreach">
                <RoleGuard allowedRoles={["admin", "user", "manager", "sales"]}><GrowthWorkspace /></RoleGuard>
              </Route>
              {/* OWNER_GROWTH_EXPORT_END */}
              <Route path="/leads/:id" component={LeadDetail} />
              <Route path="/receipts" component={AllReceipts} />
              <Route path="/receipts/upload" component={Receipts} />
              <Route path="/clients">{() => <Redirect to="/customers" />}</Route>
              <Route path="/subcontractors" component={Subcontractors} />
              <Route path="/customers/:id" component={CustomerDetail} />
              <Route path="/customers" component={Customers} />
              <Route path="/notifications" component={Notifications} />
              <Route path="/media-library" component={MediaLibrary} />
              <Route path="/project-documents" component={ProjectDocuments} />
              <Route path="/form-templates" component={FormTemplates} />
              <Route path="/drawing-tool" component={DrawingTool} />
              <Route path="/voice-recordings" component={VoiceRecordings} />
              <Route path="/master-lists" component={MasterLists} />
              <Route path="/unassigned-photos" component={UnassignedPhotos} />
              <Route path="/wc-report" component={WCReport} />
              <Route path="/daily-reports" component={DailyReports} />
              <Route path="/projects/:projectId/timeline" component={ProjectTimeline} />
              {/* Financial routes - restricted to admin/manager */}
              <Route path="/invoices">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><Invoices /></RoleGuard>
              </Route>
              <Route path="/invoice/:id/edit">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><InvoiceEditor /></RoleGuard>
              </Route>
              <Route path="/punch-list/:projectId">
                <PunchList />
              </Route>
              <Route path="/progress-billing/:projectId">
                <ProgressBilling />
              </Route>
              <Route path="/change-orders">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><ChangeOrders /></RoleGuard>
              </Route>
              <Route path="/bills">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><Bills /></RoleGuard>
              </Route>
              <Route path="/sub-bills">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><SubBills /></RoleGuard>
              </Route>
              <Route path="/vendor-bills">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><VendorBills /></RoleGuard>
              </Route>
              <Route path="/bill-queue">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><BillProcessingQueue /></RoleGuard>
              </Route>
              <Route path="/purchase-orders">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><PurchaseOrders /></RoleGuard>
              </Route>
              <Route path="/chart-of-accounts">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><ChartOfAccounts /></RoleGuard>
              </Route>
              <Route path="/job-costing">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><JobCosting /></RoleGuard>
              </Route>
              <Route path="/profit-loss">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><ProfitLoss /></RoleGuard>
              </Route>
              <Route path="/financial-summary">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><FinancialSummary /></RoleGuard>
              </Route>
              <Route path="/1099-report">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><Report1099 /></RoleGuard>
              </Route>
              <Route path="/crew-reports" component={CrewReports} />
              <Route path="/bank-accounts">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><BankAccounts /></RoleGuard>
              </Route>
              <Route path="/transactions">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><Transactions /></RoleGuard>
              </Route>
              <Route path="/sms-inquiries">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><SmsInquiriesPage /></RoleGuard>
              </Route>
              <Route path="/transaction-matching">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><TransactionMatchingPage /></RoleGuard>
              </Route>
              <Route path="/audit-history">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><AuditHistory /></RoleGuard>
              </Route>
              {/* Social Media */}
              <Route path="/social-media" component={SocialMedia} />
              <Route path="/social-accounts" component={SocialAccounts} />
              <Route path="/trash" component={Trash} />
              <Route path="/deletion-requests" component={DeletionRequests} />
              <Route path="/email-receipts" component={EmailReceipts} />
              <Route path="/activity-log" component={ActivityLog} />
              <Route path="/handbook" component={Handbook} />
              <Route path="/project-templates" component={ProjectTemplates} />
              <Route path="/recurring-expenses" component={RecurringExpenses} />
              <Route path="/budget-alerts" component={BudgetAlerts} />
              <Route path="/ai-learning" component={AILearning} />
              <Route path="/backup" component={Backup} />
              <Route path="/bid-management" component={BidManagement} />
              <Route path="/bid-tracker" component={BidTracker} />
              <Route path="/submittals" component={Submittals} />
              <Route path="/lead-sources" component={LeadSources} />
              <Route path="/realtor-outreach" component={RealtorOutreach} />
              <Route path="/banking">
                <RoleGuard allowedRoles={["admin"]}><Banking /></RoleGuard>
              </Route>
              <Route path="/client-inbox" component={ClientInbox} />
              <Route path="/email-log">
                <RoleGuard allowedRoles={["admin"]}><EmailLog /></RoleGuard>
              </Route>
              <Route path="/communications">
                <RoleGuard allowedRoles={["admin"]}><Communications /></RoleGuard>
              </Route>
              <Route path="/inbound-emails">
                <RoleGuard allowedRoles={["admin"]}><InboundEmailLog /></RoleGuard>
              </Route>
              <Route path="/commissions">
                <RoleGuard allowedRoles={["admin"]}><Commissions /></RoleGuard>
              </Route>
              <Route path="/fixed-assets">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><FixedAssets /></RoleGuard>
              </Route>
              <Route path="/balance-sheet">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><BalanceSheet /></RoleGuard>
              </Route>
              <Route path="/opening-balances">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><OpeningBalances /></RoleGuard>
              </Route>
              <Route path="/reconciliation">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><BankReconciliation /></RoleGuard>
              </Route>
              <Route path="/compliance" component={Compliance} />
              <Route path="/payment-queue">{() => { window.location.href = "/bills"; return null; }}</Route>
              <Route path="/payment-history" component={PaymentHistory} />
              <Route path="/stripe-dashboard">
                <RoleGuard allowedRoles={["admin"]}><StripeDashboard /></RoleGuard>
              </Route>
              <Route path="/tax-1099">{() => <Redirect to="/1099-report" />}</Route>
              {/* QuickBooks-style Accounting */}
              <Route path="/journal-entries">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><JournalEntries /></RoleGuard>
              </Route>
              <Route path="/account-register">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><AccountRegister /></RoleGuard>
              </Route>
              <Route path="/aging-report">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><AgingReport /></RoleGuard>
              </Route>
              {/* Material & Order Management */}
              <Route path="/order-lists" component={OrderLists} />
              <Route path="/vendor-credits" component={VendorCredits} />
              <Route path="/material-tracking" component={MaterialTracking} />
              {/* Project Knowledge Chat */}
              <Route path="/project-chat" component={ProjectChat} />
              {/* Phase 143: Financial Tools */}
              <Route path="/loans">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><LoanManager /></RoleGuard>
              </Route>
              <Route path="/sales-tax">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><SalesTax /></RoleGuard>
              </Route>
              <Route path="/mileage" component={MileageTracker} />
              <Route path="/cost-trends">
                <RoleGuard allowedRoles={FINANCIAL_ROLES}><CostTrends /></RoleGuard>
              </Route>
              <Route path="/accountant-access">
                <RoleGuard allowedRoles={["admin"]}><AccountantAccess /></RoleGuard>
              </Route>
              <Route path="/issues">
                <RoleGuard allowedRoles={["admin"]}><IssuesDashboard /></RoleGuard>
              </Route>
              {/* AI Tools */}
              <Route path="/action-queue" component={ActionQueue} />
              <Route path="/ai-chat" component={AiChat} />
              <Route path="/ai-daily-reports" component={AiDailyReports} />
              <Route path="/ai-insights" component={AiInsights} />
              <Route path="/intelligence" component={IntelligenceDashboard} />
              <Route path="/ai-memory">{() => <Redirect to="/ai-learning" />}</Route>
              <Route path="/ai-invoices" component={AiInvoices} />
              <Route path="/ai-change-orders" component={AiChangeOrders} />
              <Route path="/sub-bid-compare" component={SubBidCompare} />
              <Route path="/summary-reports" component={SummaryReports} />
              {/* Tools */}
              <Route path="/quick-actions" component={QuickActions} />
              <Route path="/client-approvals" component={ClientApprovals} />
              {/* Security & Admin */}
              <Route path="/security-center" component={SecurityCenter} />
              <Route path="/data-integrity" component={DataIntegrity} />
              <Route path="/field-permissions" component={FieldPermissions} />
              {/* Admin-only: User Management & Settings */}
              <Route path="/user-management">
                <RoleGuard allowedRoles={["admin"]}><UserManagement /></RoleGuard>
              </Route>
              <Route path="/company-settings">
                <RoleGuard allowedRoles={["admin"]}><CompanySettings /></RoleGuard>
              </Route>
              <Route path="/quickbooks-migration">
                <RoleGuard allowedRoles={["admin"]}><QuickBooksMigration /></RoleGuard>
              </Route>
              <Route path="/historical-pl">
                <RoleGuard allowedRoles={["admin"]}><HistoricalPL /></RoleGuard>
              </Route>
              <Route path="/sms-settings">
                <RoleGuard allowedRoles={["admin"]}><SmsSettings /></RoleGuard>
              </Route>
              <Route path="/sms-permissions">
                <RoleGuard allowedRoles={["admin"]}><SmsPermissions /></RoleGuard>
              </Route>
              <Route path="/sms-history">
                <RoleGuard allowedRoles={["admin"]}><SmsHistory /></RoleGuard>
              </Route>
              <Route path="/sms-photos">
                <RoleGuard allowedRoles={["admin"]}><SmsPhotoLog /></RoleGuard>
              </Route>
              <Route path="/schedule" component={Schedule} />
              <Route path="/time-tracking" component={TimeTracking} />
              <Route path="/daily-cost-log" component={DailyCostLog} />
              <Route path="/cost-plus-invoice" component={CostPlusInvoice} />
              {/* Subcontractor Portal */}
              <Route path="/sub-portal/bills" component={SubPortalBills} />
              <Route path="/sub-portal/payments" component={SubPortalPayments} />
              <Route path="/sub-portal/projects" component={SubPortalProjects} />
              <Route path="/lien-waivers" component={LienWaivers} />
              <Route path="/warranties" component={Warranties} />
              <Route path="/notification-preferences" component={NotificationPreferences} />
              <Route path="/email-templates" component={EmailTemplates} />
              <Route path="/search" component={AdvancedSearch} />
              <Route path="/404" component={NotFound} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </DashboardLayout>
      </Route>
    </Switch>
  );
}

function App() {
  useVersionCheck(30_000); // Check for new version every 30 seconds — auto-reloads on new deploy

  // Capture OAuth session token from URL param (Safari ITP workaround)
  // The OAuth callback passes the token as a query param since Safari may block cookies
  // set during cross-site redirects. We store it in localStorage and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get("__auth_token");
    if (authToken) {
      // Store in localStorage for the tRPC client to use as Authorization header
      try {
        localStorage.setItem("darkhorse_session_token", authToken);
      } catch {
        // ignore
      }
      // CRITICAL: Clear the service worker api-cache to prevent stale auth.me responses
      // Without this, the SW may return a cached "unauthenticated" response even though
      // we now have a valid token in localStorage.
      const clearAndReload = async () => {
        try {
          if ("caches" in window) {
            const cacheNames = await caches.keys();
            for (const name of cacheNames) {
              if (name.includes("api-cache")) {
                await caches.delete(name);
              }
            }
          }
        } catch {
          // ignore cache clearing errors
        }
        // Clean the URL to remove the token
        params.delete("__auth_token");
        const cleanUrl = params.toString()
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, "", cleanUrl);
        // Force a page reload to pick up the new token in tRPC headers
        window.location.reload();
      };
      clearAndReload();
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <UploadStatusBar />
          
          <Router />
          <ReportIssueButton />
          <PWAInstallPrompt />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
